
import React, { useState, useEffect, useMemo } from 'react';
import { SearchContainer, StyledTextField, SuggestionsList, SuggestionItem } from './styles';
import { Box, Typography, InputAdornment, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getAutocomplete } from '../../actions/autocomplete';
import Fuse from 'fuse.js';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

const SmallSearch = ({ onSelect }) => {
  const dispatch = useDispatch();
  const autocompleteData = useSelector((state) => state.autocomplete);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [fuzzyFallback, setFuzzyFallback] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);

  // fetch autocomplete data once
  useEffect(() => { dispatch(getAutocomplete()); }, [dispatch]);

  // debounce input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // build Fuse index
  const fuse = useMemo(() => {
    if (!autocompleteData?.length) return null;
    return new Fuse(autocompleteData, {
      keys: [
        { name: 'brandName', weight: 0.7 },
        { name: 'genericName', weight: 0.3 },
      ],
      threshold: 0.3,
      ignoreLocation: true,
    });
  }, [autocompleteData]);

  // ---- highlight helper (same as your original) ----
  const highlightText = (text, tokens, color) => {
    if (!tokens || tokens.length === 0 || !text) return text;
    const lowerText = text.toLowerCase();
    let ranges = [];
    tokens.forEach(token => {
      if (!token) return;
      let startIndex = 0;
      const lowerToken = token.toLowerCase();
      while ((startIndex = lowerText.indexOf(lowerToken, startIndex)) !== -1) {
        ranges.push({ start: startIndex, end: startIndex + token.length });
        startIndex += token.length;
      }
    });
    if (ranges.length === 0) return text;

    ranges.sort((a, b) => a.start - b.start);
    const merged = [];
    ranges.forEach(r => {
      if (!merged.length) merged.push(r);
      else {
        const last = merged[merged.length - 1];
        if (r.start <= last.end) last.end = Math.max(last.end, r.end);
        else merged.push(r);
      }
    });

    const out = [];
    let lastIdx = 0;
    merged.forEach((r, i) => {
      if (lastIdx < r.start) out.push(text.substring(lastIdx, r.start));
      out.push(
        <span key={`hl-${r.start}-${r.end}-${i}`} style={{ color }}>
          {text.substring(r.start, r.end)}
        </span>
      );
      lastIdx = r.end;
    });
    if (lastIdx < text.length) out.push(text.substring(lastIdx));
    return out;
  };

  // tokens for highlighting
  const queryTokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);

  // ---- rank + tag (same logic as Search.js) ----
  const rankAndTag = (items, q, words, mode = 'direct') => {
    const uniq = new Set();
    const out = [];

    for (const item of items) {
      const brand = (item.brandName || '').toLowerCase();
      const gen   = (item.genericName || '').toLowerCase();

      if (mode === 'direct' && !words.every(w => brand.includes(w) || gen.includes(w))) continue;

      const bPrefix  = q && brand.startsWith(q);
      const gPrefix  = q && gen.startsWith(q);
      const bContain = q && brand.includes(q);
      const gContain = q && gen.includes(q);

      let cat = 5;                 // fuzzy bucket
      if (bPrefix)       cat = 1;  // brand prefix
      else if (gPrefix)  cat = 2;  // generic prefix
      else if (bContain) cat = 3;  // brand contains
      else if (gContain) cat = 4;  // generic contains

      const matchedWith = (bPrefix || bContain) ? 'brand' : 'generic';

      const key = `${item.brandName || ''}|${item.genericName || ''}`;
      if (uniq.has(key)) continue;
      uniq.add(key);

      out.push({ ...item, matchedWith, _cat: cat });
    }

    return out.sort(
      (a, b) =>
        a._cat - b._cat ||
        (a.brandName || '').localeCompare(b.brandName || '') ||
        (a.genericName || '').localeCompare(b.genericName || '')
    );
  };

  // compute suggestions
  useEffect(() => {
    if (hasSelected) {
      setSuggestions([]);
      setFuzzyFallback(false);
      return;
    }

    const q = debouncedQuery;
    if (!q) {
      setSuggestions([]);
      setFuzzyFallback(false);
      return;
    }

    const words = q.split(/\s+/);

    // A) direct
    const directRanked = rankAndTag(autocompleteData, q, words, 'direct');
    if (directRanked.length) {
      setSuggestions(directRanked);
      setFuzzyFallback(false);
      return;
    }

    // B) fuzzy
    if (fuse) {
      const fuzzyItems = fuse.search(q, { limit: 25 }).map(r => r.item);
      const fuzzyRanked = rankAndTag(fuzzyItems, q, words, 'fuzzy');
      setSuggestions(fuzzyRanked);
      setFuzzyFallback(true);
    } else {
      setSuggestions([]);
      setFuzzyFallback(false);
    }
  }, [debouncedQuery, autocompleteData, hasSelected, fuse]);

  // select handler
  const handleSuggestionClick = (s) => {
    const displayName = s.matchedWith === 'brand' ? s.brandName : s.genericName;
    setQuery(displayName);
    setHasSelected(true);
    setSuggestions([]);
    setFuzzyFallback(false);
    onSelect?.({ id: s._id, name: displayName, genericName: s.genericName });
  };

  const handleClear = () => {
    setQuery('');
    setHasSelected(false);
    setSuggestions([]);
    setFuzzyFallback(false);
    onSelect?.({ id: '', name: '' });
  };

  // ---- UI (unchanged) ----
  return (
    <>
      <Box sx={{ display: 'flex', width: '100%', alignItems:'flex-start', flexDirection: {xs:'column', md:'row'} }}>
        <Box sx={{ width: {xs: '100%' }, marginTop: {xs:'10px', md:'0'} }}>
          <Box sx={{ display:'flex', alignItems:'center', boxSizing:'border-box', borderRadius:'16px',
            marginTop: 0, flexDirection: 'column', justifyContent: 'flex-start' }}>
            <div style={{display:'flex', alignItems:'center', flexDirection:'column', width:'100%'}}>
              <SearchContainer
                expanded={suggestions.length > 0}
                style={{display:'flex', flexDirection:'column', width:'100%', boxShadow: suggestions.length > 0 && '0px 6px 6px -2px rgba(0,0,0,0.1)'}}
              >
                <StyledTextField
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: suggestions.length > 0 ? '10px 10px 0 0' : '10px',
                      '& fieldset': { border: 'none' },
                    },
                     '& input::placeholder': {
                      fontSize: { xs: '0.85rem', md: '1rem' },
                    },
                  }}
                  variant="outlined"
                  placeholder="Start typing to select a medicine"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setHasSelected(false);
                  }}
                  showDropdown={suggestions.length > 0}
                  slotProps={{
                    input: {
                      style: {
                        color: '#333',
                        fontSize:'1rem',
                        fontWeight: '500',
                        fontFamily: 'Roboto',
                        background:'#f3f3f6',
                        border:'0 !important',
                      },
                      startAdornment: (
                        <InputAdornment position="start"><SearchIcon /></InputAdornment>
                      ),
                      endAdornment: query && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleClear}
                            sx={{ padding: 0, borderRadius: '50%', width: 24, height: 24, background:'white', color:'#333' }}
                          >
                            <CloseIcon style={{fontSize:'16px'}} />
                          </IconButton>
                        </InputAdornment>
                      )
                    },
                  }}
                />
                {suggestions.length > 0 && (
                  <SuggestionsList>
                    {fuzzyFallback && (
                      <div style={{ display: 'flex', backgroundColor: '#f1f1f1', margin: 'auto', justifyContent: 'center', padding: '20px' }}>
                        <Typography variant="subtitle1" style={{ marginBottom: '8px', color: 'grey' }}>
                          No direct matches, but we found some similar results:
                        </Typography>
                      </div>
                    )}
                    {suggestions.map((item, index) => (
                      <SuggestionItem key={index} onClick={() => handleSuggestionClick(item)}>
                        {item.matchedWith === 'brand' ? (
                          <>
                            <Typography variant="subtitle2" sx={{ fontWeight:'bold' }}>
                              {highlightText(item.brandName, queryTokens, '#059669')}
                            </Typography>
                            <Typography variant="subtitle2" style={{ fontSize: '13px', color: '#333' }}>
                              {highlightText(item.genericName, queryTokens, '#059669')}
                            </Typography>
                          </>
                        ) : (
                          <>
                          <Typography variant="subtitle2" sx={{ fontWeight:'bold' }}>
                              {highlightText(item.genericName, queryTokens, '#059669')}
                            </Typography>
                          <Typography variant="subtitle2" style={{ fontSize: '13px', color: '#333' }}>
                            {highlightText(item.genericName, queryTokens, '#3898ec')}
                          </Typography>
                          </>
                        )}
                      </SuggestionItem>
                    ))}
                  </SuggestionsList>
                )}
              </SearchContainer>
            </div>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SmallSearch;
